import React, { useState, useEffect, ChangeEvent } from 'react'
import { SelectChangeEvent } from '@mui/material/Select'
import { DatePicker } from 'rsuite'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress'
import Box from '@mui/material/Box'
import Alert from '@mui/material/Alert'
import { useAppDispatch, RootState, AppDispatch } from '../../redux/store'
import { useParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { Risque } from '../../types/risqueTypes'
import { getRisquebyId, updateRisque } from '../../redux/risques/risqueActions'
import UseAlert from '../../components/common/alert'
import SelectPlan from '../../components/common/SelectPlan'
import { PlanSummaryCard } from '../../components/quickCreate/ParentSummaryCard'
import { deadlineOutsidePlan, usePlanInfo } from '../../components/quickCreate/parentInfo'
import { formatTableDate } from '../../utils/formatDate'
import { useTranslation } from 'react-i18next'
import '../../i18n'
import SelectPriorityRisks from '../../components/common/SelectPriorityRisks'
import SelectCriticality from '../../components/common/IncidentCriticality'
import {
  FormAside,
  FormField,
  FormFoot,
  FormGrid,
  FormLayout,
  FormPageHead,
  FormSection,
  FormShell,
} from '../../components/common/form'

export default function RisqueUpdate() {
  const [risque, setRisque] = useState<Risque>({
    RisqueName: '',
    Deadline: new Date(),
    Description: '',
    PlanId: '',
  })

  const [priority, setPriority] = useState<number>(1)
  const [Criticality, setCriticality] = useState<number>(1)
  const [PlanId, setPlanId] = useState<string>('')
  const { t, i18n } = useTranslation()
  const planInfo = usePlanInfo(PlanId || undefined)

  const [alert, setAlert] = useState({
    open: false,
    message: '',
    severity: '' as 'info' | 'success' | 'error' | 'warning',
  })

  const handleCloseAlert = () => {
    setAlert({
      ...alert,
      open: false,
    })
  }
  const dispatch: AppDispatch = useAppDispatch()
  const risquesList = useSelector((state: RootState) => state.risque.risque)
  const loading = useSelector((state: RootState) => state.risque.loading)
  const error = useSelector((state: RootState) => state.risque.error)
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  useEffect(() => {
    if (id) {
      dispatch(getRisquebyId(id))
    }
  }, [dispatch, id])

  useEffect(() => {
    if (risquesList !== null) {
      setRisque({
        _id: risquesList._id,
        RisqueId: risquesList.RisqueId,
        RisqueName: risquesList.RisqueName,
        Description: risquesList.Description,
        Deadline: risquesList.Deadline,
        PlanId: risquesList.PlanId,
      })
      setPriority(risquesList.Priority as number)
      setCriticality(risquesList.Criticality as number)
      setPlanId(risquesList.PlanId || '')
    }
  }, [risquesList])

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    setRisque((prevRisque: Risque) => ({
      ...prevRisque,
      [name]: value,
    }))
  }

  const handlePlanChange = (event: SelectChangeEvent<string>) => {
    const newPlan = String(event.target.value)

    setPlanId(newPlan)

    setRisque((prevRisque: Risque) => ({
      ...prevRisque,
      PlanId: newPlan.toString(),
    }))

    console.log(PlanId)
  }

  type SingleState = string | number | string[]
  const createHandler =
    <T extends SingleState>(
      setState: React.Dispatch<React.SetStateAction<T>>,
      property: string
    ) =>
    (event: ChangeEvent<{ value: T }>) => {
      const { value } = event.target
      setState(value)
      setRisque((prevRisk) => ({
        ...prevRisk,
        [property]: value,
      }))
    }

  const handleDateChange = (value: Date | null) => {
    if (value instanceof Date && !isNaN(value.getTime())) {
      setRisque((prevRisque) => ({
        ...prevRisque,
        Deadline: value,
      }))
    }
  }

  const handlePriorityChange = createHandler(setPriority, 'Priority') as (
    event: React.ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  const handleCriticalityChange = createHandler(
    setCriticality,
    'Criticality'
  ) as (
    event: ChangeEvent<{ value: number }> | SelectChangeEvent<number>
  ) => void

  const submitLabel = t('Update Risk')

  const handleUpdateRisque = async () => {
    const plan = planInfo.data
    if (
      plan &&
      deadlineOutsidePlan(risque.Deadline, plan.DateDebut, plan.DateFin)
    ) {
      setAlert({
        open: true,
        message: t('parentLimits.deadlineOutside', {
          start: formatTableDate(plan.DateDebut, i18n.language),
          end: formatTableDate(plan.DateFin, i18n.language),
        }),
        severity: 'error',
      })
      return
    }
    try {
      if (risque.RisqueName && risque.Description && risque._id) {
        await dispatch(
          updateRisque({
            updatedRisque: risque,
            _id: risque._id,
          })
        )
        console.log(risque)

        setAlert({
          open: true,
          message: 'Risk updated successfully!',
          severity: 'success',
        })
      } else {
        setAlert({
          open: true,
          message: 'Please fill out all fields.',
          severity: 'error',
        })
      }
    } catch (error: any) {
      console.error('Error updating risk:', error.message)

      setAlert({
        open: true,
        message: 'Error updating risk Please try again later.',
        severity: 'error',
      })
    }
  }

  if (loading && !risque._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <CircularProgress sx={{ color: '#0d9b87' }} />
      </Box>
    )
  }

  if (error && !risque._id) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '50vh',
        }}
      >
        <Alert severity="error">{error}</Alert>
      </Box>
    )
  }

  return (
    <div className="page-content">
      <FormPageHead
        eyebrow={t('entityForms.risque.updateEyebrow', {
          defaultValue: 'Management · Risks',
        })}
        title={t('Update Risk')}
        subtitle={t('entityForms.risque.updateSubtitle', {
          defaultValue:
            'Update risk details, treatment plan linkage, priority and deadline.',
        })}
        backLabel={t('entityForms.risque.backToList', {
          defaultValue: 'Back to risks',
        })}
        onBack={() => navigate('/risques')}
      />

      <UseAlert
        open={alert.open}
        message={alert.message}
        severity={alert.severity}
        onClose={handleCloseAlert}
      />

      <FormLayout
        main={
          <FormShell>
            <FormSection
              title={t('entityForms.risque.sectionTitle', {
                defaultValue: 'Risk details',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Risk name')} required>
                  <input
                    className="incyia-form-inp"
                    name="RisqueName"
                    value={risque.RisqueName}
                    onChange={handleInputChange}
                    required
                  />
                </FormField>
                <FormField label={t('Treatment plan')}>
                  <SelectPlan value={PlanId} onChange={handlePlanChange} />
                </FormField>
                <FormField label={t('Description')} required span>
                  <textarea
                    className="incyia-form-inp"
                    name="Description"
                    value={risque.Description}
                    onChange={handleInputChange as any}
                    rows={4}
                    required
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormSection
              title={t('entityForms.risque.assessmentTitle', {
                defaultValue: 'Assessment',
              })}
            >
              <FormGrid cols={2}>
                <FormField label={t('Priority')}>
                  <SelectPriorityRisks
                    value={priority}
                    onChange={handlePriorityChange}
                  />
                </FormField>
                <FormField label={t('Criticality')}>
                  <SelectCriticality
                    value={Criticality}
                    onChange={handleCriticalityChange}
                  />
                </FormField>
                <FormField label={t('Deadline')} span>
                  {/* TODO(backend): a risk does not return its linked tasks, so the deadline cannot be checked against those task dates. */}
                  <DatePicker
                    value={
                      risque.Deadline ? new Date(risque.Deadline) : null
                    }
                    onChange={handleDateChange}
                    style={{ width: '100%' }}
                    shouldDisableDate={(date) =>
                      deadlineOutsidePlan(
                        date,
                        planInfo.data?.DateDebut,
                        planInfo.data?.DateFin
                      )
                    }
                  />
                </FormField>
              </FormGrid>
            </FormSection>

            <FormFoot
              note={t('actionCreate.aside.requiredMark', {
                defaultValue: '* Required',
              })}
              cancelLabel={t('Cancel')}
              submitLabel={submitLabel}
              onCancel={() => navigate('/risques')}
              onSubmit={handleUpdateRisque}
            />
          </FormShell>
        }
        aside={
          <FormAside
            title={t('actionCreate.aside.inThisForm', {
              defaultValue: 'In this form',
            })}
            tipTitle={t('actionCreate.aside.goodToKnow', {
              defaultValue: 'Good to know',
            })}
            tip={t('entityForms.risque.tip', {
              defaultValue:
                'Risks are tracked against their treatment plan and feed into compliance dashboards.',
            })}
            context={
              PlanId ? (
                <>
                  <PlanSummaryCard plain plan={planInfo.data} status={planInfo.status} />
                  <p className="incyia-aside-note incyia-aside-divider">
                    {t('parentLimits.stayInside')}
                  </p>
                </>
              ) : undefined
            }
            steps={[
              {
                title: t('entityForms.risque.sectionTitle', {
                  defaultValue: 'Risk details',
                }),
                fieldCount: 3,
              },
              {
                title: t('entityForms.risque.assessmentTitle', {
                  defaultValue: 'Assessment',
                }),
                fieldCount: 3,
              },
            ]}
            requiredCount={2}
            submitLabel={submitLabel}
            fieldsLabel={(n) =>
              t(
                n === 1
                  ? 'actionCreate.aside.fields_one'
                  : 'actionCreate.aside.fields_other',
                { count: n, defaultValue: `${n} fields` }
              )
            }
            requiredNote={(count, submit) =>
              t('actionCreate.aside.requiredNote', {
                count,
                submit,
                defaultValue: `${count} required before you can ${submit}.`,
              })
            }
          />
        }
      />
    </div>
  )
}
